import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import cx from 'clsx';

import type { TSelectContext } from './context';
import { SelectContext } from './context';
import Option from './Option';

import NavigationIcon from '~/assets/icons/ic_outline_chevron_down.svg';

import styles from './Select.module.css';

type Props<T extends unknown> = {
  selectedName?: string;
  defaultOpened?: boolean;
  outsideClickClosed?: boolean;
  disabled?: boolean;
  isWarn?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
} & TSelectContext<T>;

const Select = <T extends unknown>({
  selectedName,
  placeholder,
  defaultOpened = false,
  outsideClickClosed = true,
  disabled = false,
  isWarn = false,
  children,
  className,
  style,
  ...props
}: Props<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(defaultOpened);

  const onClickContent = (event: MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;

    setOpened(!opened);
  };

  useEffect(() => {
    // Select 바깥 영역 클릭시 Select가 닫히게 함.
    function handleClickOutside(event: globalThis.MouseEvent) {
      event.stopPropagation();
      if (disabled) return;

      if (
        outsideClickClosed &&
        event.target &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpened(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [containerRef, outsideClickClosed, disabled]);

  return (
    <SelectContext.Provider value={{ ...props } as TSelectContext<unknown>}>
      <div
        className={cx(
          styles.selectWrap,
          isWarn && styles.warn,
          disabled && styles.disabled,
          className,
        )}
        style={style}
        role="presentation"
        onClick={onClickContent}
      >
        <div className={styles.selected} ref={containerRef}>
          <span className={styles.placeholder}>
            {selectedName || placeholder}
          </span>
          <NavigationIcon
            className={cx([
              opened && styles.openIcon,
              disabled && styles.disabled,
            ])}
          />
        </div>
        <div className={cx([styles.optionWrap, !opened && styles.hide])}>
          {children}
        </div>
      </div>
    </SelectContext.Provider>
  );
};

Select.displayName = 'Select';

export default Select;

Select.Option = Option;
